import { BRAND, DEFAULT_ZONE_KIND_COLOR, ZONE_KIND_COLORS, type ColorMode } from '@chukua/common';
import type { ExpressionSpecification, LayerSpecification, Map as MapLibreMap } from 'maplibre-gl';
import type { ZoneKind } from '../../../../reference-data/reference-data.api';

/** Polygons, as drawn. */
export const ZONE_SHAPES_SOURCE_ID = 'zone-shapes';
/** The circle of the point zone being edited (useZoneRadiusPreview); empty while no point zone's panel is open. */
export const ZONE_EDIT_RADIUS_SOURCE_ID = 'zone-edit-radius';
/** A dot per zone (a polygon's marker, or a named point), clustered while zoomed out. */
export const ZONE_POINTS_SOURCE_ID = 'zone-points';

/** The layers a pointer can hover or click to reach one zone. */
export const ZONE_HIT_LAYER_IDS = ['zone-fills', 'zone-points'];
/** The cluster bubbles, which zoom in on click. */
export const ZONE_CLUSTER_LAYER_ID = 'zone-clusters';

/**
 * Below this zoom a polygon zone shows as its marker dot; from it up, as the polygon itself. At the
 * zoom the whole corridor fits in, a 200 m yard is well under a pixel.
 */
const MARKER_MAX_ZOOM = 13;

/** Clustering stops a zoom short of the polygons taking over, so the dots have separated by the time they fade. */
export const ZONE_POINTS_SOURCE = {
  type: 'geojson',
  cluster: true,
  clusterRadius: 40,
  clusterMaxZoom: MARKER_MAX_ZOOM - 1,
} as const;

/** The layers whose colour follows the zone's kind — repainted when the kind catalogue arrives or changes. */
export const KIND_COLORED_LAYERS = [
  { id: 'zone-fills', property: 'fill-color' },
  { id: 'zone-outlines', property: 'line-color' },
  { id: 'zone-points', property: 'circle-color' },
] as const;

/** Fonts the MapTiler streets style serves glyphs for; a symbol layer naming any other draws no text. */
const LABEL_FONT = ['Roboto Medium', 'Noto Sans Regular'];

/** How much the other zones fade while one is open, so the one being edited is clearly the subject. */
const DIMMED_OPACITY = 0.35;

const isCluster: ExpressionSpecification = ['has', 'point_count'];

/** A polygon's marker fades out as its polygon becomes visible; a named point's dot stays at `opacity`. */
function fadesWithPolygon(opacity: number): ExpressionSpecification {
  return ['interpolate', ['linear'], ['zoom'], MARKER_MAX_ZOOM - 1, opacity, MARKER_MAX_ZOOM, ['case', ['get', 'isMarker'], 0, opacity]];
}

/**
 * A zone's colour: its kind's shade for the colour mode, or muted grey once the zone is deactivated. A kind
 * missing from the catalogue (`zones.kind` has no FK) falls back to the default colour rather than failing to draw.
 */
export function zoneColorExpression(zoneKinds: ZoneKind[], mode: ColorMode): ExpressionSpecification {
  const fallback = ZONE_KIND_COLORS[DEFAULT_ZONE_KIND_COLOR][mode];
  const inactive = BRAND.palettes[mode].textMuted;
  if (zoneKinds.length === 0) return ['case', ['get', 'isActive'], fallback, inactive];
  // MapLibre types `match` with its label/output pairs spelled out; built from the catalogue at runtime, they go through unknown.
  const byKind = [
    'match',
    ['get', 'kind'],
    ...zoneKinds.flatMap((kind) => [kind.code, ZONE_KIND_COLORS[kind.color][mode]]),
    fallback,
  ] as unknown as ExpressionSpecification;
  return ['case', ['get', 'isActive'], byKind, inactive];
}

export interface ZoneOpacityPaint {
  layerId: string;
  property: Parameters<MapLibreMap['setPaintProperty']>[1];
  value: number | ExpressionSpecification;
}

/** The opacities of the zone layers, full or dimmed while another zone is open. Set on the live map as that changes. */
export function zoneOpacityPaint(isDimmed: boolean): ZoneOpacityPaint[] {
  const scale = isDimmed ? DIMMED_OPACITY : 1;
  return [
    { layerId: 'zone-fills', property: 'fill-opacity', value: 0.25 * scale },
    { layerId: 'zone-outlines', property: 'line-opacity', value: scale },
    { layerId: 'zone-points', property: 'circle-opacity', value: fadesWithPolygon(scale) },
    { layerId: 'zone-points', property: 'circle-stroke-opacity', value: fadesWithPolygon(scale) },
    { layerId: ZONE_CLUSTER_LAYER_ID, property: 'circle-opacity', value: 0.85 * scale },
    { layerId: ZONE_CLUSTER_LAYER_ID, property: 'circle-stroke-opacity', value: scale },
    { layerId: 'zone-cluster-counts', property: 'text-opacity', value: scale },
  ];
}

interface ZoneLayerOptions {
  mode: ColorMode;
  isDimmed: boolean;
}

/**
 * Polygons as a tinted fill with an outline; single zones as dots in their kind's colour; and, while zoomed
 * out, overlapping dots as a neutral bubble with a count, since a cluster can mix kinds. All in the colour
 * mode's palette, as the basemap under them is.
 */
export function zoneLayers(zoneKinds: ZoneKind[], { mode, isDimmed }: ZoneLayerOptions): LayerSpecification[] {
  const color = zoneColorExpression(zoneKinds, mode);
  const { text, surface } = BRAND.palettes[mode];
  const layers: LayerSpecification[] = [
    { id: 'zone-fills', type: 'fill', source: ZONE_SHAPES_SOURCE_ID, paint: { 'fill-color': color } },
    { id: 'zone-outlines', type: 'line', source: ZONE_SHAPES_SOURCE_ID, paint: { 'line-color': color, 'line-width': 1.5 } },
    {
      id: 'zone-points',
      type: 'circle',
      source: ZONE_POINTS_SOURCE_ID,
      filter: ['!', isCluster],
      paint: { 'circle-color': color, 'circle-radius': 6, 'circle-stroke-color': surface, 'circle-stroke-width': 1.5 },
    },
    {
      id: ZONE_CLUSTER_LAYER_ID,
      type: 'circle',
      source: ZONE_POINTS_SOURCE_ID,
      filter: isCluster,
      paint: {
        'circle-color': text,
        'circle-radius': ['step', ['get', 'point_count'], 12, 10, 16, 25, 20],
        'circle-stroke-color': surface,
        'circle-stroke-width': 2,
      },
    },
    {
      id: 'zone-cluster-counts',
      type: 'symbol',
      source: ZONE_POINTS_SOURCE_ID,
      filter: isCluster,
      layout: { 'text-field': ['get', 'point_count_abbreviated'], 'text-font': LABEL_FONT, 'text-size': 12, 'text-allow-overlap': true },
      paint: { 'text-color': surface },
    },
  ];
  for (const { layerId, property, value } of zoneOpacityPaint(isDimmed)) {
    const layer = layers.find((candidate) => candidate.id === layerId);
    if (layer && 'paint' in layer) (layer.paint as Record<string, unknown>)[property] = value;
  }
  return layers;
}

/** The edited point zone's circle, in the accent terracotta the panel's terra-draw shapes use, dashed so it reads as a reach rather than a drawn edge. */
export function editRadiusLayers(mode: ColorMode): LayerSpecification[] {
  const { accent } = BRAND.palettes[mode];
  return [
    { id: 'zone-edit-radius-fill', type: 'fill', source: ZONE_EDIT_RADIUS_SOURCE_ID, paint: { 'fill-color': accent, 'fill-opacity': 0.2 } },
    {
      id: 'zone-edit-radius-outline',
      type: 'line',
      source: ZONE_EDIT_RADIUS_SOURCE_ID,
      paint: { 'line-color': accent, 'line-width': 2, 'line-dasharray': [2, 1] },
    },
  ];
}
